import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import ResearchLayout, { useReveal } from "../ResearchLayout";

/* Animated counter */
function Counter({ end, suffix = "", duration = 2000 }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !started.current) {
            started.current = true;
            const start = performance.now();
            const animate = (t) => {
              const p = Math.min((t - start) / duration, 1);
              const eased = 1 - Math.pow(1 - p, 3);
              setVal(Math.floor(end * eased));
              if (p < 1) requestAnimationFrame(animate);
            };
            requestAnimationFrame(animate);
          }
        });
      },
      { threshold: 0.4 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [end, duration]);

  return (
    <span ref={ref}>
      {val}
      {suffix}
    </span>
  );
}

const STATS = [
  { num: 9, suffix: "+", label: "Clinical Trials" },
  { num: 940, suffix: "+", label: "Patients Enrolled" },
  { num: 1800, suffix: "+", label: "Patients Screened" },
  { num: 8, suffix: "", label: "International Sponsors" },
];

const FEATURES = [
  {
    icon: "fa-shield-check",
    title: "GCP Certified Team",
    text: "All physicians, nurses, and coordinators are Good Clinical Practice certified, ensuring international standards of safety and ethics.",
  },
  {
    icon: "fa-globe",
    title: "Global Collaborations",
    text: "Active partnerships with McMaster University, Queen Mary University London, AIM Vaccine, Bill & Melinda Gates Foundation and more.",
  },
  {
    icon: "fa-microscope",
    title: "DRAP Approved Site",
    text: "Maroof International Hospital is a DRAP-approved clinical trial site for conducting trials in Pakistan.",
  },
  {
    icon: "fa-user-doctor",
    title: "Expert Investigators",
    text: "A dedicated team of principal investigators across cardiology, hepatology, neurology, oncology and more.",
  },
  {
    icon: "fa-clipboard-list",
    title: "Ethics & Compliance",
    text: "IRB registered with the U.S. Office for Human Research Protections (IORG0011525, FWA00032840).",
  },
  {
    icon: "fa-graduation-cap",
    title: "Training & Capacity Building",
    text: "Workshops, seminars, and research grant management training for clinicians and research officers.",
  },
];

const HIGHLIGHT_TRIALS = [
  { name: "Hepatitis C", sponsor: "Atea Pharmaceuticals", status: "Ongoing" },
  { name: "London-Pakistan Parkinson Project", sponsor: "Queen Mary University", status: "Ongoing" },
  { name: "HIP ATTACK 2", sponsor: "McMaster University", status: "Ongoing" },
  { name: "ASPIREAF", sponsor: "McMaster University", status: "Ongoing" },
  { name: "LVRNA021 COVID Booster", sponsor: "AIM Vaccine, China", status: "Completed" },
  { name: "REVISE", sponsor: "McMaster University", status: "Completed" },
];

export default function ResearchHome() {
  useReveal();

  return (
    <ResearchLayout>
      {/* ─── HERO ─── */}
      <section
        style={{
          position: "relative",
          minHeight: "92vh",
          display: "flex",
          alignItems: "center",
          background:
            "linear-gradient(135deg, #0d3b6e 0%, #076cec 50%, #04b3bf 100%)",
          backgroundSize: "200% 200%",
          animation: "rsGradientShift 15s ease infinite",
          overflow: "hidden",
          color: "#fff",
        }}
      >
        {/* Animated blobs */}
        <div
          className="rs-blob"
          style={{ width: 500, height: 500, background: "#04b3bf", top: "-100px", right: "-100px" }}
        />
        <div
          className="rs-blob"
          style={{ width: 400, height: 400, background: "#ff6b35", bottom: "-100px", left: "-100px", animationDelay: "2s" }}
        />
        <div
          className="rs-blob"
          style={{ width: 300, height: 300, background: "#f7c948", top: "30%", left: "40%", animationDelay: "4s", opacity: 0.25 }}
        />

        {/* Floating molecules */}
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            style={{
              position: "absolute",
              width: 8 + Math.random() * 12,
              height: 8 + Math.random() * 12,
              borderRadius: "50%",
              background: "rgba(255,255,255,0.3)",
              top: `${Math.random() * 100}%`,
              left: `${Math.random() * 100}%`,
              animation: `rsFloat ${4 + Math.random() * 4}s ease-in-out infinite`,
              animationDelay: `${Math.random() * 2}s`,
            }}
          />
        ))}

        <div
          className="rs-container"
          style={{ position: "relative", zIndex: 2, paddingTop: 80, paddingBottom: 80 }}
        >
          <div
            style={{ animation: "rsFadeUp 0.8s ease both" }}
          >
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "8px 18px",
                borderRadius: 50,
                background: "rgba(255,255,255,0.15)",
                backdropFilter: "blur(10px)",
                border: "1px solid rgba(255,255,255,0.25)",
                fontSize: "0.78rem",
                fontWeight: 700,
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                marginBottom: 24,
              }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: "#7ce28e",
                  animation: "rsPulse 2s ease-in-out infinite",
                }}
              />
              DRAP Approved · NBC Authorized · IRB Registered
            </span>
          </div>

          <h1
            style={{
              fontSize: "clamp(2.4rem, 6vw, 4.5rem)",
              fontWeight: 800,
              lineHeight: 1.05,
              margin: "0 0 24px",
              maxWidth: 900,
              animation: "rsFadeUp 0.8s 0.15s ease both",
            }}
          >
            Advancing Healthcare Through{" "}
            <span
              style={{
                background: "linear-gradient(90deg, #f7c948, #ff6b35)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Clinical Research
            </span>
          </h1>

          <p
            style={{
              fontSize: "clamp(1rem, 1.6vw, 1.25rem)",
              lineHeight: 1.7,
              opacity: 0.9,
              maxWidth: 720,
              margin: "0 0 40px",
              animation: "rsFadeUp 0.8s 0.3s ease both",
            }}
          >
            Maroof International Hospital's Clinical Research Department is one of Pakistan's most trusted and high-performing research centers — driving innovation through ethical, internationally compliant clinical trials.
          </p>

          <div
            style={{
              display: "flex",
              gap: 16,
              flexWrap: "wrap",
              animation: "rsFadeUp 0.8s 0.45s ease both",
            }}
          >
            <Link
              to="/research/clinical-trials"
              className="rs-btn"
              style={{
                background: "#fff",
                color: "var(--rs-primary)",
                boxShadow: "0 12px 30px rgba(0,0,0,0.2)",
              }}
            >
              <i className="fa-regular fa-microscope"></i>
              Explore Our Trials
            </Link>
            <Link
              to="/research/director-message"
              className="rs-btn"
              style={{
                background: "rgba(255,255,255,0.12)",
                color: "#fff",
                border: "2px solid rgba(255,255,255,0.4)",
                backdropFilter: "blur(10px)",
              }}
            >
              <i className="fa-regular fa-quote-right"></i>
              Director's Message
            </Link>
          </div>
        </div>

        {/* Scroll indicator */}
        <div
          style={{
            position: "absolute",
            bottom: 30,
            left: "50%",
            transform: "translateX(-50%)",
            color: "rgba(255,255,255,0.6)",
            fontSize: "0.75rem",
            letterSpacing: "0.2em",
            textTransform: "uppercase",
            animation: "rsFloat 2s ease-in-out infinite",
            zIndex: 2,
          }}
        >
          <div style={{ textAlign: "center" }}>
            <i className="fa-regular fa-chevron-down" style={{ fontSize: "1.2rem" }}></i>
            <div style={{ marginTop: 6 }}>Scroll</div>
          </div>
        </div>
      </section>

      {/* ─── STATS ─── */}
      <section style={{ padding: "60px 0", background: "#f8fafc" }}>
        <div className="rs-container">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: 24,
            }}
          >
            {STATS.map((s, i) => (
              <div
                key={s.label}
                className={`rs-reveal delay-${i + 1}`}
                style={{
                  background: "#fff",
                  borderRadius: 18,
                  padding: 32,
                  textAlign: "center",
                  border: "1px solid rgba(7, 108, 236,0.08)",
                  transition: "all 0.3s",
                }}
              >
                <div
                  style={{
                    fontSize: "clamp(2rem, 4vw, 3rem)",
                    fontWeight: 800,
                    background: "var(--rs-grad-1)",
                    WebkitBackgroundClip: "text",
                    backgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    lineHeight: 1,
                    marginBottom: 8,
                  }}
                >
                  <Counter end={s.num} suffix={s.suffix} />
                </div>
                <div style={{ fontSize: "0.875rem", color: "#6b7280", fontWeight: 600 }}>
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── FEATURES ─── */}
      <section className="rs-section">
        <div className="rs-container">
          <div className="rs-text-center rs-reveal">
            <span className="rs-section-eyebrow">Why Maroof Research</span>
            <h2 className="rs-section-title">
              Built on <span className="accent">Ethics, Excellence & Innovation</span>
            </h2>
            <p className="rs-section-sub">
              Our greatest strength lies in our people — a dedicated team that works with precision and compassion to ensure the highest standards of research ethics and patient safety.
            </p>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(290px, 1fr))",
              gap: 24,
            }}
          >
            {FEATURES.map((f, i) => (
              <div key={f.title} className={`rs-card rs-reveal delay-${(i % 4) + 1}`}>
                <div className="rs-card-icon">
                  <i className={`fa-regular ${f.icon}`}></i>
                </div>
                <h3 className="rs-card-title">{f.title}</h3>
                <p className="rs-card-text">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── TRIALS HIGHLIGHT ─── */}
      <section
        className="rs-section"
        style={{
          background:
            "linear-gradient(135deg, #f0f7ff 0%, #e6fffa 100%)",
          position: "relative",
        }}
      >
        <div className="rs-container">
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 60,
              alignItems: "center",
            }}
            className="rs-grid-stack"
          >
            <div className="rs-reveal">
              <span className="rs-section-eyebrow">Active & Completed</span>
              <h2 className="rs-section-title">
                Pioneering <span className="accent">Clinical Trials</span> Across Disciplines
              </h2>
              <p style={{ color: "#6b7280", lineHeight: 1.75, fontSize: "1rem", marginBottom: 28 }}>
                From Hepatitis C and Parkinson's research to COVID-19 vaccine trials and orthopedic studies — we collaborate with leading global institutions to bring breakthrough treatments to patients.
              </p>
              <Link to="/research/clinical-trials" className="rs-btn rs-btn-primary">
                View All Trials
                <i className="fa-regular fa-arrow-right"></i>
              </Link>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {HIGHLIGHT_TRIALS.map((t, i) => (
                <div
                  key={t.name}
                  className={`rs-reveal delay-${(i % 4) + 1}`}
                  style={{
                    background: "#fff",
                    padding: "18px 22px",
                    borderRadius: 14,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 14,
                    boxShadow: "0 4px 14px rgba(7, 108, 236,0.06)",
                    transition: "all 0.3s",
                    borderLeft: "4px solid var(--rs-secondary)",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateX(8px)";
                    e.currentTarget.style.boxShadow = "0 10px 30px rgba(7, 108, 236,0.15)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateX(0)";
                    e.currentTarget.style.boxShadow = "0 4px 14px rgba(7, 108, 236,0.06)";
                  }}
                >
                  <div>
                    <div style={{ fontWeight: 700, color: "var(--rs-dark)", fontSize: "0.95rem" }}>
                      {t.name}
                    </div>
                    <div style={{ fontSize: "0.78rem", color: "#6b7280", marginTop: 3 }}>
                      {t.sponsor}
                    </div>
                  </div>
                  <span
                    className={`rs-status rs-status-${t.status.toLowerCase()}`}
                  >
                    {t.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <style>{`
          @media (max-width: 900px) {
            .rs-grid-stack { grid-template-columns: 1fr !important; gap: 40px !important; }
          }
        `}</style>
      </section>

      {/* ─── CTA ─── */}
      <section
        style={{
          padding: "80px 0",
          background: "linear-gradient(135deg, #0d3b6e 0%, #076cec 100%)",
          color: "#fff",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          className="rs-blob"
          style={{ width: 500, height: 500, background: "#04b3bf", top: "-200px", right: "-200px", opacity: 0.3 }}
        />

        <div className="rs-container rs-text-center" style={{ position: "relative", zIndex: 2 }}>
          <div className="rs-reveal">
            <h2
              style={{
                fontSize: "clamp(1.8rem, 4vw, 2.8rem)",
                fontWeight: 800,
                margin: "0 0 16px",
              }}
            >
              Partner With Us
            </h2>
            <p style={{ fontSize: "1.05rem", opacity: 0.9, maxWidth: 640, margin: "0 auto 36px", lineHeight: 1.7 }}>
              Whether you're a sponsor, researcher, clinician, or patient — join us on this journey of discovery, innovation, and service.
            </p>
            <div style={{ display: "inline-flex", gap: 14, flexWrap: "wrap", justifyContent: "center" }}>
              <Link
                to="/research/contact"
                className="rs-btn"
                style={{ background: "#fff", color: "var(--rs-primary)" }}
              >
                <i className="fa-regular fa-envelope"></i>
                Contact Research Team
              </Link>
              <Link
                to="/research/team"
                className="rs-btn"
                style={{
                  background: "transparent",
                  color: "#fff",
                  border: "2px solid rgba(255,255,255,0.5)",
                }}
              >
                Meet the Team
              </Link>
            </div>
          </div>
        </div>
      </section>
    </ResearchLayout>
  );
}
